/**
 * dsh-work — 远程工作区桥接：判定当前会话 cwd 是否落在 dsh-remote 的
 * 镜像目录之下，并给出数据源 URL 前缀。
 *
 *  - 命中镜像根 → 前缀 '/dsh-remote'（目录树/文件读写/资产/终端改走
 *    dsh-remote 宿主半的远程数据路由，路径继续用镜像形态，UI 无感）。
 *  - 未命中或插件缺席 → 前缀 ''（照旧走本地 /workbench/*）。
 *
 * 健壮性：档案列表不能「一次失败 = 永久无远程」——dsh web 重启窗口内
 * 的瞬时失败会让目录树静默落到本地空镜像（表现为树变空）。网络层失败
 * 按退避重试（2s 起步、封顶 15s，成功即止）；只有明确的 404（插件
 * 确实未安装）才判定缺席。列表到达后 remoteBase 变化会自动触发目录树
 * 重取（listDir 依赖 remoteBase），无需额外刷新。
 */
import React from 'react'
import { remoteBaseOf } from './helpers.js'

const { useEffect, useState } = React

/**
 * hook：返回 [base, mirrors]。
 * mirrors 为 null 表示尚未加载（按无远程处理）；加载失败持续退避重试。
 */
export function useRemoteBase(cwd) {
	const [mirrors, setMirrors] = useState(null)
	useEffect(() => {
		let stopped = false
		let attempt = 0
		let timer = undefined
		const load = () => {
			fetch('/dsh-remote/list', { headers: { accept: 'application/json' } })
				.then((response) => {
					if (stopped) return null
					if (response.status === 404) {
						setMirrors([]) // 插件确实未安装：短路，不再重试
						return null
					}
					if (!response.ok) throw new Error('HTTP ' + String(response.status))
					return response.json()
				})
				.then((payload) => {
					if (stopped || payload === null) return
					if (payload.ok === true && Array.isArray(payload.profiles)) {
						setMirrors(payload.profiles.map((p) => p.mirrorPath).filter((m) => typeof m === 'string'))
					} else {
						setMirrors([])
					}
				})
				.catch(() => {
					if (stopped) return
					// 瞬时失败（如 dsh web 重启窗口）：退避重试，不把一次失败当成永久缺席。
					attempt += 1
					timer = setTimeout(load, Math.min(2000 * attempt, 15000))
				})
		}
		load()
		return () => {
			stopped = true
			if (timer !== undefined) clearTimeout(timer)
		}
	}, [])
	return [remoteBaseOf(cwd, mirrors ?? []), mirrors]
}
